import { FC, Fragment, useEffect, useState } from 'react'

import {
  Card,
  SquareDotLoadingIndicator,
  TextButton,
} from '@joor/design-system'
import classNames from 'classnames'
import isEmpty from 'lodash/isEmpty'
import sumBy from 'lodash/sumBy'
import { useParams } from 'react-router-dom'

import {
  ManualPaymentMethod,
  ManualTransactionFragment,
} from '__generated__/atlas-types'

import { Flex } from 'styles/components'
import { FORMAT_MM_DD_YYYY, formatDate } from 'utils/dates'
import { decimalWithCommas } from 'utils/formatters'
import { useUserAnalytics } from 'utils/sdks/userAnalytics'

import { useGetManualTransactions } from '../../PublicInvoice.queries'
import { refetchRemainingBalance } from '../helpers'
import { useOrderRemainingBalance } from '../payment.hooks'
import { TransactionHistoryGrid } from '../styles'
import CreateEditManualTransactionModal from './CreateEditManualTransactionModal'
import { Order } from 'features/Orders/types'

export const ManualPaymentMethodMapping = {
  [ManualPaymentMethod.CHECK]: 'Check',
  [ManualPaymentMethod.CASH]: 'Cash',
  [ManualPaymentMethod.CREDIT_CARD]: 'Credit Card',
  [ManualPaymentMethod.CREDIT_MEMO]: 'Credit Memo',
  [ManualPaymentMethod.INVOICE_FACTORING]: 'Invoice Factoring',
  [ManualPaymentMethod.WIRE]: 'Wire',
  [ManualPaymentMethod.BANK_TRANSFER]: 'Bank Transfer',
  [ManualPaymentMethod.OTHER]: 'Other',
}

const EMPTY_PARAM_CHAR = '—'

type ManualTransactionsOrderInfo = Pick<
  Order,
  | 'id'
  | 'currencyCode'
  | 'retailer'
  | 'brand'
  | 'lineItems'
  | 'shippingPrice'
  | 'taxAmount'
  | 'discountedTotalRange'
>

interface PropsType {
  orderInfo: ManualTransactionsOrderInfo
  accountId: string
  isBrand?: boolean
}

const ManualTransactions: FC<PropsType> = ({
  orderInfo,
  accountId,
  isBrand = false,
}) => {
  const [isCreateEditModalOpen, setIsCreateEditModalOpen] = useState(false)
  const { trackEvent } = useUserAnalytics()
  const { uuid } = useParams<{ uuid?: string }>()
  const { data: manualTransactions } = useGetManualTransactions({
    accountId,
    orderId: orderInfo.id,
  })
  const { remainingBalance, loading } = useOrderRemainingBalance(orderInfo.id)

  const [selectedTransaction, setSelectedTransactionn] =
    useState<ManualTransactionFragment>()

  useEffect(() => {
    if (manualTransactions !== undefined && orderInfo.id) {
      // When we have an update in manualTransactions we need to do a refetch of the query
      // OrderPaymentInfoDocument to have the updated remaining_balance
      // This useEffect should be replaced with a poll in the remaining balance query
      // This useEffect is also covering the case when a Brand made a Manual payment and the
      // retailer has the payment open, it will automatically update the remaining balance
      refetchRemainingBalance({ isPublic: !!uuid })
    }
  }, [manualTransactions, orderInfo.id])

  if (loading) {
    return (
      <Card title="Manual Transactions">
        <Flex justifyContent="center" alignItems="center">
          <SquareDotLoadingIndicator />
        </Flex>
      </Card>
    )
  }

  const totalManualPaidAmount = sumBy(
    manualTransactions,
    ({ amount }: any) => amount,
  )

  const handleOpenModal = (manualTransaction?: ManualTransactionFragment) => {
    setSelectedTransactionn(manualTransaction)
    setIsCreateEditModalOpen(true)
  }

  if (!isBrand && !manualTransactions?.length) {
    return null
  }

  return (
    <Card
      title="Manual Transactions"
      {...(isBrand &&
        remainingBalance > 0 && {
          actionButtonLabel: 'Create',
          onClickActionButton: () => {
            handleOpenModal()
            trackEvent('JP_Click_Create_ManualPayment')
          },
        })}
    >
      {isCreateEditModalOpen && (
        <CreateEditManualTransactionModal
          isOpen={isCreateEditModalOpen}
          setIsOpen={setIsCreateEditModalOpen}
          orderInfo={orderInfo}
          manualTransaction={selectedTransaction}
          remainingAmount={remainingBalance}
        />
      )}
      {isEmpty(manualTransactions) ? (
        <span>No manual payments recorded</span>
      ) : (
        <TransactionHistoryGrid gridTemplateColumns="repeat(5, 1fr)">
          <span className="header with-bottom-border">Received Date</span>
          <span className="header with-bottom-border">Reference #</span>
          <span className="header with-bottom-border">Payment Method</span>
          <span className="header with-bottom-border">Recorded by</span>
          <span className="header with-bottom-border text-right">
            Received Amount
          </span>
          {manualTransactions?.map((manualTransaction, idx: number) => {
            const {
              id,
              paymentMethod,
              paymentDate,
              amount,
              currency,
              recordedBy,
              referenceNumber,
            } = manualTransaction
            return (
              <Fragment key={id}>
                <span
                  className={classNames('cell', {
                    'with-bottom-border': idx !== manualTransactions.length - 1,
                  })}
                >
                  {formatDate(paymentDate, FORMAT_MM_DD_YYYY, true)}
                </span>
                <span
                  className={classNames('cell', {
                    'with-bottom-border': idx !== manualTransactions.length - 1,
                  })}
                >
                  {referenceNumber || EMPTY_PARAM_CHAR}
                </span>
                <span
                  className={classNames('cell', {
                    'with-bottom-border': idx !== manualTransactions.length - 1,
                  })}
                >
                  {ManualPaymentMethodMapping[paymentMethod]}
                </span>
                <span
                  className={classNames('cell', {
                    'with-bottom-border': idx !== manualTransactions.length - 1,
                  })}
                >
                  {recordedBy || EMPTY_PARAM_CHAR}
                </span>
                <span
                  className={classNames('cell text-right no-wrap', {
                    'with-bottom-border': idx !== manualTransactions.length - 1,
                  })}
                >
                  {isBrand ? (
                    <TextButton
                      onClick={() => handleOpenModal(manualTransaction)}
                    >
                      {decimalWithCommas(amount)} {currency}
                    </TextButton>
                  ) : (
                    `${decimalWithCommas(amount)} ${currency}`
                  )}
                </span>
              </Fragment>
            )
          })}
          <div className="total-header cell grid-column-5">Paid</div>
          <div className="total text-right cell">{`${decimalWithCommas(
            totalManualPaidAmount,
          )} ${orderInfo.currencyCode}`}</div>
        </TransactionHistoryGrid>
      )}
    </Card>
  )
}

export default ManualTransactions
